/* eslint-disable statura/no-floating-point-money --
 * Canonical recall-to-work pay calculator. Every $-arithmetic line is
 * regulated under one of the four supported awards:
 *   - Nurses MA000034 cl 19.1 + 19.2 + 19.6 + 19.7 (FWO library K700647 +
 *     FWC consolidated PDF cl 19.2(a) OT bands)
 *   - SCHADS MA000100 cl 28.1 + 28.4 (FWC consolidated PDF; cl 28.1(a)(i)
 *     2h tier-1 boundary disability/home care/day care; cl 28.1(a)(ii) 3h
 *     tier-1 boundary social/community/crisis accommodation FT)
 *   - Aged Care MA000018 cl 25.1(a) + 25.1(e) (FWC consolidated PDF;
 *     cl 25.1(a)(i)(A) Mon-Fri tiered; cl 25.1(a)(i)(B) Saturday FLAT 200%
 *     grouped with Sunday — NOT banded)
 *   - HPSS MA000027 cl 25.2 + 25.3 + 25.6 (FWC consolidated PDF; cl 25.2
 *     FT/PT OT bands; cl 25.3 casual OT bands; cl 25.6 recall trigger).
 *
 * Each engine declares its own `RecallRules` constant binding the
 * jurisdiction-specific knobs (minimum hours, banded OT rate table,
 * carveouts) and delegates to `calculateRecallPay` here.
 *
 * Phase Codex Pass A PR #B (2026-05-19) — Banded OT recall rebuild
 * per FWC primary source. Pre-Phase-Codex-A-B `calculateRecallPay`
 * collapsed each recall event to a single multiplier (tier-1 1.5x for
 * Mon-Sat, flat 2.0x Sunday, flat 2.5x PH) — under-paid permanent + casual
 * workers across all 4 awards when paidHours exceeded the tier-1 boundary
 * (e.g. AC 4h Mon-Fri recall got 4h x 1.5x = 600% instead of 2h x 1.5x +
 * 2h x 2.0x = 700%). Casual workers were ALSO under-paid because the
 * default multiplier-lookup fallthrough returned 1.5x (permanent) instead
 * of 1.875x (casual MULTIPLICATIVE per AC cl 25.1 NOTE / HPSS cl 25.3
 * NOTE / Nurses cl 19.2(a) "% of casual hourly rate" / SCHADS settled
 * doctrine). Verified by statura-regulatory-source-agent against FWC
 * consolidated PDFs, artifact at
 * docs/operations/clause-verifications/2026-05-19-statura-regulatory-source-recall-banded-ot.md.
 *
 * Critical NEW findings from specialist (NOT in original Codex brief):
 *
 *   - **AC MA000018 Saturday recall is FLAT 200%**, NOT banded. cl
 *     25.1(a)(i)(B) groups Saturday with Sunday at flat 200% — only
 *     Mon-Fri (cl 25.1(a)(i)(A)) follows the tiered curve. This is the
 *     9th confirmed cross-award shape-port instance: porting the
 *     Nurses/HPSS/SCHADS Mon-Sat tiered shape onto AC would UNDER-pay
 *     Saturday recall for the first 2h (1.5x instead of 2.0x).
 *
 *   - **SCHADS FT social/community/crisis-accommodation has 3h tier-1
 *     boundary**, NOT 2h. cl 28.1(a)(ii). Disability / home care / day
 *     care FT plus all PT plus all casual = 2h tier-1 boundary
 *     (cl 28.1(a)(i)). Stream-aware dispatch required.
 *
 *   - **Casual loading is MULTIPLICATIVE across all 4 awards**, not
 *     "INCLUSIVE". The pre-Phase-Codex-A-B "INCLUSIVE" treatment for
 *     AC / HPSS / SCHADS silently underpaid casuals by 25% on the OT
 *     multiplier when the ctx penalty band didn't supply a casual row
 *     (which is the production reality — ctx penalties carry ordinary-hour
 *     casual rates, not OT casual rates).
 */

/**
 * Phase 1C (Award Engine Integrity 2026-05) — shared recall-to-work
 * utility. Consumed by all four engines via the `calculateRecall` method
 * on the `AwardEngine` interface (Phase 1C interface extension).
 *
 * Phase Codex Pass A PR #B (2026-05-19) — extended with banded OT
 * support (`OtRateBands` per award + per-employment-type) replacing the
 * pre-Phase-Codex-A-B `otBandOverrides` single-multiplier mapping.
 *
 * What this utility produces:
 *
 *   - `paidHours` — actual time-worked equivalent for the recall, after
 *     applying the award's minimum-payment guarantee.
 *   - `paidAmount` — dollar pay at the appropriate OT band(s). For
 *     weekday/Saturday recalls that cross the tier-1 boundary, this is
 *     the sum of `paidHours_tier1 x baseHourlyRate x tier1Multiplier`
 *     plus `paidHours_tier2 x baseHourlyRate x tier2Multiplier`. For
 *     Sunday, PH, and AC Saturday recalls, the rate is flat (no banding).
 *   - `lines[]` — itemised breakdown lines (one per tier band that fired).
 *     Each line carries `hours`, `multiplier`, `amount`, and `clauseRef`
 *     so audit / coordinator UI can show "first 2h @ 150%, next 2h @ 200%"
 *     for a 4h recall.
 *   - `bandApplied` — which broad OT band drove the rate (audit field).
 *     For banded outputs this is set to `'OT_TIER_1'` (the dominant band
 *     that triggered the calculation).
 *   - `multiplierApplied` — for banded outputs, this is the EFFECTIVE
 *     multiplier (paidAmount / (paidHours x baseHourlyRate)) — useful
 *     for compact UI display. The full per-band detail is in `lines[]`.
 *   - `clauseRef` — top-level citation; `lines[].clauseRef` carries
 *     per-tier citations.
 *   - `warnings[]` — any unusual conditions (e.g. recall within 3h of
 *     rostered start → travel-time-only carveout applied).
 *
 * Per-award scenarios:
 *
 *   1. 30-min in-person recall → 3h minimum at OT (Nurses cl 19.6,
 *      "minimum payment of 3 hours' work at the appropriate overtime
 *      rate").
 *
 *   2. 30-min electronic recall → 1h minimum at OT (Nurses cl 19.7).
 *
 *   3. Multiple electronic calls within 1h → collapse to a single 1h
 *      minimum (per FWO interpretation that an "occurrence" is a
 *      contiguous recall episode, not a per-call event).
 *
 *   4. Recall within 3h of rostered start → only travel time counts; no
 *      minimum-payment carveout (Nurses cl 19.7(d) only — cl 19.6 has no
 *      equivalent under primary-source per Phase 1J MD-A5).
 *
 * See:
 *   - `award-engine-integrity-architecture-2026-05-15.md` §1.3
 *   - FWO library K700647 (Nurses recall worked example)
 *   - docs/operations/clause-verifications/2026-05-19-statura-regulatory-source-recall-banded-ot.md
 */

import {
  COVERAGE_GAP_MULTIPLIER,
  coverageGapsField,
  isRequiredPenaltyMissing,
  pushPenaltyCoverageGap,
  resolveRequiredPenalty,
  type EbaCoverageGap,
  type PenaltyBand,
  type PenaltyLine,
  type RateContext,
} from '../award-rate-lookup';
import { roundMoney } from '../money-rounding';
import type { AwardCode } from './types';

/** Recall classification — drives the minimum-payment band selection. */
export type RecallType = 'IN_PERSON' | 'ELECTRONIC';

/**
 * Award-specific pay band the recall is paid at. All four supported
 * awards use a similar band selection logic but differ in their tier-1
 * boundary, Saturday treatment (AC = flat, others = tiered Mon-Sat),
 * and casual loading treatment (MULTIPLICATIVE across all 4 post-Phase-
 * Codex-A-B).
 *
 * Phase Codex Pass A PR #B (2026-05-19) — added `OT_TIER_2` for the
 * banded-OT path. `OT_TIER_1` is still used as the dominant band on
 * the top-level breakdown; `OT_TIER_2` appears on per-band lines.
 * Also added `OT_SATURDAY_AC` to discriminate AC's flat-200% Saturday
 * from the tiered Mon-Sat shape used by Nurses / HPSS / SCHADS.
 */
export type RecallPayBand =
  | 'OT_TIER_1'
  | 'OT_TIER_2'
  | 'OT_SUNDAY'
  | 'OT_PUBLIC_HOLIDAY'
  | 'OT_SATURDAY_AC';

export interface RecallPayInput {
  recallType: RecallType;
  /** When the recall was triggered (drives day-of-week → band). */
  recalledAt: Date;
  /** IANA timezone for day-of-week. Defaults to Australia/Sydney. */
  timezone?: string;
  /** True if the recall date is a declared public holiday. */
  isPublicHoliday?: boolean;

  // ─── IN_PERSON fields ────────────────────────────────────────────────
  /** Arrival at workplace; required for IN_PERSON. */
  workplaceArrivalAt?: Date;
  /** Departure from workplace; required for IN_PERSON. */
  workplaceDepartureAt?: Date;
  /** One-way travel time TO the workplace in minutes. */
  travelTimeToMinutes?: number;
  /** One-way travel time FROM the workplace in minutes. */
  travelTimeFromMinutes?: number;

  // ─── ELECTRONIC fields ───────────────────────────────────────────────
  /** Call start; required for ELECTRONIC. */
  electronicCallStartedAt?: Date;
  /** Call end; required for ELECTRONIC. */
  electronicCallEndedAt?: Date;
  /**
   * Additional contiguous calls within the minimum-payment window
   * (collapsed to the single minimum per FWO interpretation). Reported
   * for audit but does not multiply the minimum.
   */
  electronicSubsequentCallCount?: number;

  // ─── Context fields ──────────────────────────────────────────────────
  /** Whether the worker was on-call when recalled. */
  wasOnCall: boolean;
  /**
   * Was the worker called in within Nh of their rostered shift start?
   * When true, only travel time counts; no minimum payment applies
   * (Nurses cl 19.7(d) and equivalents).
   */
  calledWithin3hOfRosteredStart: boolean;

  // ─── Stream-aware fields (Phase Codex Pass A PR #B, 2026-05-19) ──────
  /**
   * Worker's classification stream. ONLY consumed by the SCHADS engine
   * today, to discriminate the tier-1 boundary:
   *   - 'sacs' (Social, Community Services, Crisis Accommodation) FT
   *     → 3h tier-1 boundary per cl 28.1(a)(ii).
   *   - 'home_care_disability', 'home_care_aged', or any other stream
   *     FT → 2h tier-1 boundary per cl 28.1(a)(i).
   *   - PT / casual any stream → 2h tier-1 boundary per cl 28.1(b)(i).
   *
   * Other awards ignore this field. Required for SCHADS FT workers; if
   * missing for a SCHADS FT worker, `calculateRecallPay` throws a
   * `RecallBandLookupError`. PT / casual SCHADS workers can omit.
   *
   * Source: FWC consolidated MA000100 cl 28.1(a) + Pay Guide G01002983
   * "Overtime - Monday to Saturday - first 3 hours (full-time) or first
   * 2 hours (disability services and part-time)".
   */
  classificationStream?: string;
  /**
   * Whether the worker is full-time for OT purposes. Consumed by the
   * SCHADS engine for tier-1 boundary discrimination (FT social/community/
   * crisis accommodation = 3h; FT disability/home care/day care = 2h;
   * PT/casual = 2h). The flag is sourced from
   * `staff.employment_type === 'FULL_TIME'` (see SCHADS caller at
   * `apps/web/src/lib/payroll/recall-actions.ts`).
   */
  isFullTimeForOvertime?: boolean;
}

/**
 * Per-employment-type OT multiplier pair (tier-1 + tier-2 for the
 * weekday/Saturday banded path).
 */
export interface OtBandPair {
  /** Permanent (FT/PPT) multiplier on top of `baseHourlyRate`. */
  permanent: number;
  /**
   * Casual multiplier on top of `baseHourlyRate`. Note this is the FINAL
   * casual multiplier (already incorporates the 25% casual loading), so
   * for tier-1 OT this is typically 1.5 x 1.25 = 1.875.
   *
   * Per AC cl 25.1 NOTE: "the casual loading ... is added to ... before
   * applying the overtime rate". Per HPSS cl 25.3 NOTE: same shape. Per
   * Nurses cl 19.2(a): "X% of the casual hourly rate". SCHADS: settled
   * doctrine via FWC + FWO + multiple compliance guides.
   */
  casual: number;
}

/**
 * Award-specific OT rate band table. Lives at the file end of each
 * `award-data/<award>.ts` file as `<AWARD>_OT_RATE_BANDS`.
 *
 * Phase Codex Pass A PR #B (2026-05-19) — REPLACES the pre-Phase-Codex-
 * A-B `otBandOverrides` single-multiplier mapping. Captures both
 * tier-1 + tier-2 multipliers per employment type, plus the
 * award-specific Saturday treatment (AC = flat; Nurses/HPSS/SCHADS =
 * tiered).
 *
 * Tier-1 boundary semantics:
 *   - `weekdayTier1Hours = 2` — first 2h of OT @ tier-1 multiplier,
 *     hours 2+ @ tier-2 multiplier. Applies to AC (cl 25.1(a)(i)(A) FT),
 *     Nurses (cl 19.2(a)(i) all employment types), HPSS (cl 25.2(b)(i)
 *     FT/PT + cl 25.3(b)(i) casual), SCHADS (cl 28.1(a)(i) FT
 *     disability/home care/day care + cl 28.1(b)(i) PT/casual any
 *     stream).
 *   - `weekdayTier1Hours = 3` — first 3h of OT @ tier-1, hours 3+ @
 *     tier-2. Applies ONLY to SCHADS cl 28.1(a)(ii) FT social/community
 *     services / crisis accommodation. Stream-aware dispatch via
 *     `SchadsEngine.calculateRecall` is required.
 *
 * Saturday discrimination:
 *   - When `saturdayFlat` is set (AC only), Saturday recall pays the
 *     `saturdayFlat` rate for ALL hours (no tier banding). Verified
 *     against MA000018 cl 25.1(a)(i)(B): "Saturday or Sunday — 200%".
 *   - When `saturdayFlat` is undefined (Nurses, HPSS, SCHADS), Saturday
 *     joins the weekday tier curve via `weekdayTier1Hours` /
 *     `weekdayTier1` / `weekdayTier2`. Verified against MA000034 cl
 *     19.1(a)(i) (Saturday grouped with Mon-Fri), MA000027 cl 25.2 /
 *     25.3 (no Saturday carveout), MA000100 cl 28.1(a) (no Saturday
 *     carveout).
 *
 * Source: FWC consolidated PDFs per award (artifact §1-4).
 */
export interface OtRateBands {
  /** Tier-1 boundary in hours (typically 2; SCHADS FT SACS = 3). */
  weekdayTier1Hours: number;
  /** Mon-Fri (+ Saturday for non-AC) tier-1 multiplier per employment type. */
  weekdayTier1: OtBandPair;
  /** Mon-Fri (+ Saturday for non-AC) tier-2 multiplier per employment type. */
  weekdayTier2: OtBandPair;
  /** Sunday multiplier (flat, no banding) per employment type. */
  sunday: OtBandPair;
  /** Public holiday multiplier (flat, no banding) per employment type. */
  publicHoliday: OtBandPair;
  /**
   * AC ONLY: Saturday FLAT multiplier per employment type, grouped with
   * Sunday in cl 25.1(a)(i)(B). When this field is present, Saturday
   * recall pays the flat rate (no banding). When absent, Saturday joins
   * the weekday tier curve. Nurses / HPSS / SCHADS omit this field.
   */
  saturdayFlat?: OtBandPair;
  /**
   * Per-tier clause citations. Each tier's emitted breakdown line
   * (`RecallPayLine`) carries the corresponding clauseRef so audit / UI
   * can show "first 2h @ 150% (cl 25.1(a)(i)(A))" etc.
   */
  clauseRefs: {
    weekdayTier1: string;
    weekdayTier2: string;
    sunday: string;
    publicHoliday: string;
    saturdayFlat?: string;
  };
}

export interface RecallRules {
  awardCode: AwardCode;
  /** Minimum payment for IN_PERSON recall, in hours (typically 3.0). */
  inPersonMinimumHours: number;
  /** Minimum payment for ELECTRONIC recall, in hours (typically 1.0). */
  electronicMinimumHours: number;
  /** When true, travel time counts as time worked (Nurses cl 19.7(c)). */
  travelCountsAsTimeWorked: boolean;
  /**
   * Subsequent electronic work beyond the minimum is rounded UP to the
   * next N minutes (typically 15 — Nurses cl 19.7). Currently informational
   * since we collapse to the minimum; placeholder for future
   * past-minimum extensions.
   */
  electronicRoundingMinutes: number;
  /**
   * Within-this-many-hours of rostered start → travel only, no minimum
   * (Nurses cl 19.7(d)). Typical 3.0.
   */
  withinRosteredStartCarveoutHours: number;
  /**
   * Casual loading treatment for the recall pay calculation.
   *
   * Phase Codex Pass A PR #B (2026-05-19) — extensively revised. All four
   * awards now use MULTIPLICATIVE casual loading per the specialist
   * primary-source verification (AC cl 25.1 NOTE / HPSS cl 25.3 NOTE /
   * Nurses cl 19.2(a) / SCHADS settled doctrine). When `otBands` is
   * supplied (the post-Phase-Codex-A-B path), the casual multipliers are
   * read directly from `otBands.*.casual` and this field is informational
   * only (kept for backward-compat with legacy callers).
   *
   * Values (legacy meaning, retained for backward-compat):
   *   - 'ADDITIVE'      — rate = `mult + 0.25`. Deprecated;
   *                       no engine uses this post-Phase-Codex-A-B.
   *   - 'INCLUSIVE'     — rate = `mult` (ctx penalty band's CASUAL row
   *                       already encodes the final casual rate).
   *                       Deprecated for recall-OT path; the AC / HPSS /
   *                       SCHADS pre-Phase-Codex-A-B "INCLUSIVE" setting
   *                       was wrong because the ctx penalty bands carry
   *                       ordinary-hour casual rates, not OT casual rates
   *                       — fell through to permanent multiplier and
   *                       under-paid casuals by 25% on every recall.
   *   - 'MULTIPLICATIVE' — rate = `mult × 1.25`. Primary-source-correct
   *                       across all 4 awards. Legacy path (when
   *                       `otBands` is absent) uses this constant to
   *                       compute the casual rate from the permanent
   *                       multiplier.
   */
  casualLoadingTreatment: 'ADDITIVE' | 'INCLUSIVE' | 'MULTIPLICATIVE';
  /**
   * Phase Codex Pass A PR #B (2026-05-19) — per-award OT rate band table.
   * REPLACES the pre-Phase-Codex-A-B `otBandOverrides` single-multiplier
   * mapping. When present, the recall calculator uses banded math (tier-1
   * boundary, tier-2 split, AC Saturday flat). When absent (legacy path),
   * the calculator falls back to `otBandOverrides` / ctx-penalty lookup
   * with a default tier-1 multiplier of 1.5 — that path is now
   * fail-closed for casual workers under awards with `'INCLUSIVE'` or
   * `'MULTIPLICATIVE'` treatment when no penalty line matches (was
   * silently 1.5).
   */
  otBands?: OtRateBands;
  /**
   * @deprecated Phase Codex Pass A PR #B (2026-05-19) — superseded by
   * `otBands` (full tier-1 + tier-2 + per-employment-type table). Kept
   * for backward-compat during the PR rollout; engines should populate
   * `otBands` instead. When both are present, `otBands` wins.
   */
  otBandOverrides?: Partial<Record<RecallPayBand, number>>;
  /**
   * Phase 1J (Award Engine Integrity 2026-05-16) — Nurses-specific
   * scope-gating for cl 19.6 vs cl 19.7. When true, `travelCountsAsTimeWorked`
   * and `withinRosteredStartCarveoutHours` apply ONLY when the worker
   * was NOT on-call at the time of the recall (i.e. cl 19.7 path).
   * When the worker WAS on-call (cl 19.6 path), travel time is NOT
   * counted as time worked and the within-3h carveout does NOT fire —
   * cl 19.6 has no equivalent provisions per primary text (Cluster A
   * residual sweep). See errata catalogue MD-NURSES-A4 + MD-NURSES-A5.
   */
  onCallScopeGating?: boolean;
  /** Quoted in the breakdown for audit. */
  clauseRef: string;
}

/**
 * Per-tier breakdown line in a banded recall breakdown. One line per
 * tier band that fired. Emitted in `RecallPayBreakdown.lines[]`.
 *
 * For weekday/Saturday recalls that cross the tier-1 boundary (e.g. AC
 * 4h Mon-Fri recall), two lines are emitted:
 *   { code: 'RECALL_PAY_TIER1', hours: 2, multiplier: 1.5, amount: ..., clauseRef: 'AC cl 25.1(a)(i)(A) first 2h' }
 *   { code: 'RECALL_PAY_TIER2', hours: 2, multiplier: 2.0, amount: ..., clauseRef: 'AC cl 25.1(a)(i)(A) after 2h' }
 *
 * For flat-band recalls (Sunday, PH, AC Saturday), a single line is
 * emitted with the corresponding band code.
 */
export interface RecallPayLine {
  /**
   * Line code identifying which band this line covers. Stable values:
   *   - 'RECALL_PAY_TIER1' — first N hours of OT (banded Mon-Sat or
   *     AC Mon-Fri only).
   *   - 'RECALL_PAY_TIER2' — hours N+ of OT (banded Mon-Sat or AC
   *     Mon-Fri only).
   *   - 'RECALL_PAY_SATURDAY_FLAT' — AC Saturday flat (cl 25.1(a)(i)(B)).
   *   - 'RECALL_PAY_SUNDAY_FLAT' — all awards Sunday flat.
   *   - 'RECALL_PAY_PUBLIC_HOLIDAY_FLAT' — all awards PH flat.
   */
  code:
    | 'RECALL_PAY_TIER1'
    | 'RECALL_PAY_TIER2'
    | 'RECALL_PAY_SATURDAY_FLAT'
    | 'RECALL_PAY_SUNDAY_FLAT'
    | 'RECALL_PAY_PUBLIC_HOLIDAY_FLAT';
  /** Hours covered by this band. */
  hours: number;
  /** OT multiplier applied to `baseHourlyRate` for this band. */
  multiplier: number;
  /** Dollar amount for this line (hours × baseHourlyRate × multiplier). */
  amount: number;
  /** Award clause citation for this band. */
  clauseRef: string;
}

export interface RecallPayBreakdown {
  /** Paid hours after minimum-payment / carveout rules applied. */
  paidHours: number;
  /** Dollar pay before any pay-run-level deductions or allowance caps. */
  paidAmount: number;
  /**
   * Which dominant OT band selected the multiplier (audit field). For
   * banded outputs this is set to the band of the tier-1 line.
   */
  bandApplied: RecallPayBand;
  /**
   * For banded outputs, the EFFECTIVE multiplier (paidAmount /
   * (paidHours × baseHourlyRate)) — useful for compact UI display. The
   * full per-band detail is in `lines[]`. For single-band outputs
   * (Sunday/PH/AC-Sat), this is the flat multiplier.
   */
  multiplierApplied: number;
  /**
   * Phase Codex Pass A PR #B (2026-05-19) — itemised per-tier breakdown.
   * One line per tier band that fired. ALWAYS populated for non-zero
   * recalls under the banded path; legacy path emits a single line with
   * the historical band code.
   */
  lines: RecallPayLine[];
  /** Top-level award clause citation. Per-tier citations are in `lines[]`. */
  clauseRef: string;
  /** Whether the within-rostered-start carveout was applied. */
  carveoutApplied: boolean;
  /** EBA strict-mode gaps that make this recall non-persistable. */
  coverageGaps?: EbaCoverageGap[];
  warnings: string[];
}

/**
 * Phase Codex Pass A PR #B (2026-05-19) — sentinel error thrown when a
 * banded-OT calculation cannot proceed because a required field is
 * missing. Caller is expected to surface this to the coordinator UI as
 * "recall pay cannot be calculated: missing classification stream" etc.
 *
 * Distinct error class so callers can catch and degrade gracefully
 * (e.g. `forecast-actions.ts` fail-soft) without swallowing real bugs.
 *
 * Replaces the pre-Phase-Codex-A-B silent fallthrough to multiplier=1.5
 * that under-paid casuals across 3 of 4 awards.
 */
export class RecallBandLookupError extends Error {
  readonly kind = 'RecallBandLookupError' as const;
  constructor(message: string) {
    super(message);
    this.name = 'RecallBandLookupError';
  }
}

/* ═══════════════════════════════════════════════════════════════════════
 * Internal helpers
 * ═══════════════════════════════════════════════════════════════════════ */

const CASUAL_LOADING = 0.25;

const RECALL_PAY_BAND_TO_PENALTY_BAND: Record<RecallPayBand, PenaltyBand> = {
  OT_TIER_1: 'OVERTIME_FIRST_2H',
  OT_TIER_2: 'OVERTIME_AFTER_2H',
  OT_SUNDAY: 'SUNDAY',
  OT_PUBLIC_HOLIDAY: 'PUBLIC_HOLIDAY',
  OT_SATURDAY_AC: 'SATURDAY',
};

function getDayOfWeek(d: Date, tz: string): number {
  const fmt = new Intl.DateTimeFormat('en-AU', { timeZone: tz, weekday: 'short' });
  const short = fmt.format(d);
  return (
    {
      Sun: 0,
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
    } as Record<string, number>
  )[short]!;
}

/**
 * Day-category discriminant for the banded calculation. PH wins over
 * any day-of-week per FW Act + every award text. Saturday-for-AC is
 * special-cased only when `rules.otBands.saturdayFlat` is set; for the
 * other 3 awards, Saturday joins the weekday tier curve.
 */
type DayCategory = 'public_holiday' | 'sunday' | 'saturday_ac_flat' | 'weekday_tiered';

function selectDayCategory(
  recalledAt: Date,
  tz: string,
  isPublicHoliday: boolean,
  bands: OtRateBands,
): DayCategory {
  if (isPublicHoliday) return 'public_holiday';
  const dow = getDayOfWeek(recalledAt, tz);
  if (dow === 0) return 'sunday';
  if (dow === 6 && bands.saturdayFlat) return 'saturday_ac_flat';
  // dow in {1..5} OR (dow=6 AND no saturdayFlat) → tiered Mon-Sat path
  return 'weekday_tiered';
}

/**
 * Determine SCHADS tier-1 boundary from stream + employment type.
 *
 * SCHADS cl 28.1 splits the tier-1 boundary by employment type AND, for
 * full-timers, by stream:
 *   - FT social/community services / crisis accommodation (SACS stream)
 *     → 3h tier-1 (cl 28.1(a)(ii)).
 *   - FT disability services / home care / day care
 *     → 2h tier-1 (cl 28.1(a)(i)).
 *   - PT or casual, any stream
 *     → 2h tier-1 (cl 28.1(b)(i)).
 *
 * Stream values in the live DB (verified 2026-05-19 against
 * `award_rate_lines.classification_stream` for MA000100):
 *   'home_care_aged', 'home_care_disability', 'sacs'.
 *
 * The 'sacs' stream covers Social and Community Services Sector
 * employees (Schedule B classification). Crisis Accommodation
 * Employees (Schedule C) share the same FT 3h boundary per cl 28.1(a)(ii)
 * — when crisis-accommodation classifications are added to the DB, they
 * should be seeded under stream='sacs' OR an alias that this function
 * recognises.
 *
 * Returns the tier-1 boundary in hours.
 *
 * Phase Codex Pass A PR #B (2026-05-19) — fail-closed when stream
 * missing for an FT SCHADS worker. Replaces the pre-Phase-Codex-A-B
 * silent fallthrough to flat 1.5x on every hour.
 */
function resolveSchadsTier1Hours(
  classificationStream: string | undefined,
  isFullTimeForOvertime: boolean | undefined,
  bands: OtRateBands,
): number {
  // PT / casual → always 2h tier-1 per cl 28.1(b)(i). The bands struct's
  // default (2h) is correct for this case.
  if (!isFullTimeForOvertime) return 2;
  // FT SACS / crisis accommodation → 3h tier-1 per cl 28.1(a)(ii).
  // Stream MUST be supplied for FT SCHADS workers; missing stream is
  // fail-closed below to surface coordinator data-quality issues.
  if (classificationStream === undefined || classificationStream === null) {
    throw new RecallBandLookupError(
      `SCHADS MA000100 cl 28.1 full-time recall requires classificationStream to determine ` +
        `tier-1 boundary (sacs/crisis-accommodation = 3h per cl 28.1(a)(ii); ` +
        `disability/home-care/day-care = 2h per cl 28.1(a)(i)).`,
    );
  }
  const sacsStreams = new Set(['sacs', 'crisis_accommodation', 'social_community']);
  if (sacsStreams.has(classificationStream)) return 3;
  // FT disability / home care / day care → 2h per cl 28.1(a)(i).
  // Use the bands struct's default (2h) — same value as PT/casual.
  return bands.weekdayTier1Hours;
}

function lookupOtMultiplierLegacy(
  ctx: RateContext,
  band: RecallPayBand,
  employmentType: 'PERMANENT' | 'CASUAL',
  rules: RecallRules,
  coverageGaps: EbaCoverageGap[],
): number {
  // Legacy path — used only when `rules.otBands` is undefined. Kept for
  // backward-compat with any engine that hasn't migrated. Phase Codex
  // Pass A PR #B (2026-05-19) — all four engines now ship `otBands`, so
  // this path should never fire in production. Retained for safety.

  const penaltyBandKey = RECALL_PAY_BAND_TO_PENALTY_BAND[band];
  const lookup = (requiredBand: string, employment: 'PERMANENT' | 'CASUAL') =>
    ctx.penalties.find(
      (p: PenaltyLine) => p.band === requiredBand && p.employmentType === employment,
    )?.multiplier ?? null;

  const found = ctx.penalties.find(
    (p: PenaltyLine) => p.band === penaltyBandKey && p.employmentType === employmentType,
  );
  if (found) return found.multiplier;

  if (rules.otBandOverrides && band in rules.otBandOverrides) {
    const override = rules.otBandOverrides[band];
    if (typeof override === 'number') {
      return resolveRequiredPenalty(ctx, penaltyBandKey, employmentType, override, {
        lookup,
        coverageGaps,
      });
    }
  }

  const missingRequired = resolveRequiredPenalty(ctx, penaltyBandKey, employmentType, Number.NaN, {
    lookup,
    coverageGaps,
  });
  if (coverageGaps.length > 0) return missingRequired;

  // Phase Codex Pass A PR #B (2026-05-19) — pre-fix this returned 1.5 as
  // a silent fallthrough, under-paying casuals across all 4 awards under
  // the legacy path. Post-fix: fail-closed.
  throw new RecallBandLookupError(
    `Recall band ${band} multiplier not found in rules.otBandOverrides or ctx.penalties for ` +
      `${rules.awardCode} ${employmentType}. Cannot fall through to default — pre-Phase-Codex-A-B ` +
      `silent fallthrough caused systematic casual under-pay across all four awards.`,
  );
}

function computeRawHoursInPerson(input: RecallPayInput, travelCounts: boolean): number {
  // Workplace time worked.
  let workHours = 0;
  if (input.workplaceArrivalAt && input.workplaceDepartureAt) {
    const ms = input.workplaceDepartureAt.getTime() - input.workplaceArrivalAt.getTime();
    workHours = Math.max(0, ms / (1000 * 60 * 60));
  }
  // Travel time, when counted.
  const travelMinutes = (input.travelTimeToMinutes ?? 0) + (input.travelTimeFromMinutes ?? 0);
  const travelHours = travelMinutes / 60;
  return workHours + (travelCounts ? travelHours : 0);
}

function computeRawHoursElectronic(input: RecallPayInput): number {
  if (!input.electronicCallStartedAt || !input.electronicCallEndedAt) return 0;
  const ms = input.electronicCallEndedAt.getTime() - input.electronicCallStartedAt.getTime();
  return Math.max(0, ms / (1000 * 60 * 60));
}

function travelOnlyHours(input: RecallPayInput): number {
  const travelMinutes = (input.travelTimeToMinutes ?? 0) + (input.travelTimeFromMinutes ?? 0);
  return travelMinutes / 60;
}

/* ═══════════════════════════════════════════════════════════════════════
 * Banded-OT helpers (Phase Codex Pass A PR #B, 2026-05-19)
 * ═══════════════════════════════════════════════════════════════════════ */

function pickBandPair(pair: OtBandPair, employmentType: 'PERMANENT' | 'CASUAL'): number {
  return employmentType === 'CASUAL' ? pair.casual : pair.permanent;
}

function resolveRecallMultiplier(
  ctx: RateContext,
  band: RecallPayBand,
  employmentType: 'PERMANENT' | 'CASUAL',
  hardcodedFallback: number,
  coverageGaps: EbaCoverageGap[],
  options: { allowEbaOverride?: boolean } = {},
): number {
  const penaltyBand = RECALL_PAY_BAND_TO_PENALTY_BAND[band];
  if (!options.allowEbaOverride) {
    if (isRequiredPenaltyMissing(ctx, penaltyBand, employmentType)) {
      pushPenaltyCoverageGap(coverageGaps, penaltyBand, employmentType);
      return COVERAGE_GAP_MULTIPLIER;
    }
    return hardcodedFallback;
  }

  return resolveRequiredPenalty(ctx, penaltyBand, employmentType, hardcodedFallback, {
    coverageGaps,
  });
}

/**
 * Build the breakdown lines for a banded-OT recall.
 *
 * For Sunday / PH / AC Saturday (flat-band paths): a single line.
 *
 * For Mon-Fri (AC) or Mon-Sat (Nurses/HPSS/SCHADS) (tiered paths):
 *   - If paidHours <= tier1Boundary: a single tier-1 line.
 *   - Else: two lines — tier-1 (boundary hours) + tier-2 (overflow hours).
 */
function buildBandedLines(
  dayCategory: DayCategory,
  paidHours: number,
  baseHourlyRate: number,
  ctx: RateContext,
  employmentType: 'PERMANENT' | 'CASUAL',
  bands: OtRateBands,
  tier1Boundary: number,
  coverageGaps: EbaCoverageGap[],
): RecallPayLine[] {
  if (paidHours <= 0) return [];

  switch (dayCategory) {
    case 'public_holiday': {
      const mult = resolveRecallMultiplier(
        ctx,
        'OT_PUBLIC_HOLIDAY',
        employmentType,
        pickBandPair(bands.publicHoliday, employmentType),
        coverageGaps,
      );
      return [
        {
          code: 'RECALL_PAY_PUBLIC_HOLIDAY_FLAT',
          hours: roundHours(paidHours),
          multiplier: mult,
          amount: roundCents(paidHours * baseHourlyRate * mult),
          clauseRef: bands.clauseRefs.publicHoliday,
        },
      ];
    }
    case 'sunday': {
      const mult = resolveRecallMultiplier(
        ctx,
        'OT_SUNDAY',
        employmentType,
        pickBandPair(bands.sunday, employmentType),
        coverageGaps,
      );
      return [
        {
          code: 'RECALL_PAY_SUNDAY_FLAT',
          hours: roundHours(paidHours),
          multiplier: mult,
          amount: roundCents(paidHours * baseHourlyRate * mult),
          clauseRef: bands.clauseRefs.sunday,
        },
      ];
    }
    case 'saturday_ac_flat': {
      if (!bands.saturdayFlat) {
        // Defensive — selectDayCategory only returns this when saturdayFlat
        // is defined. Throwing here makes the invariant load-bearing.
        throw new RecallBandLookupError(
          'Day category saturday_ac_flat but otBands.saturdayFlat is undefined — engine misconfiguration.',
        );
      }
      const mult = resolveRecallMultiplier(
        ctx,
        'OT_SATURDAY_AC',
        employmentType,
        pickBandPair(bands.saturdayFlat, employmentType),
        coverageGaps,
      );
      return [
        {
          code: 'RECALL_PAY_SATURDAY_FLAT',
          hours: roundHours(paidHours),
          multiplier: mult,
          amount: roundCents(paidHours * baseHourlyRate * mult),
          clauseRef:
            bands.clauseRefs.saturdayFlat ??
            'AC MA000018 cl 25.1(a)(i)(B) Saturday/Sunday flat 200%',
        },
      ];
    }
    case 'weekday_tiered': {
      const tier1Mult = resolveRecallMultiplier(
        ctx,
        'OT_TIER_1',
        employmentType,
        pickBandPair(bands.weekdayTier1, employmentType),
        coverageGaps,
        { allowEbaOverride: true },
      );

      if (paidHours <= tier1Boundary) {
        return [
          {
            code: 'RECALL_PAY_TIER1',
            hours: roundHours(paidHours),
            multiplier: tier1Mult,
            amount: roundCents(paidHours * baseHourlyRate * tier1Mult),
            clauseRef: bands.clauseRefs.weekdayTier1,
          },
        ];
      }

      const tier1Hours = tier1Boundary;
      const tier2Hours = paidHours - tier1Boundary;
      const tier2Mult = resolveRecallMultiplier(
        ctx,
        'OT_TIER_2',
        employmentType,
        pickBandPair(bands.weekdayTier2, employmentType),
        coverageGaps,
        { allowEbaOverride: true },
      );
      return [
        {
          code: 'RECALL_PAY_TIER1',
          hours: roundHours(tier1Hours),
          multiplier: tier1Mult,
          amount: roundCents(tier1Hours * baseHourlyRate * tier1Mult),
          clauseRef: bands.clauseRefs.weekdayTier1,
        },
        {
          code: 'RECALL_PAY_TIER2',
          hours: roundHours(tier2Hours),
          multiplier: tier2Mult,
          amount: roundCents(tier2Hours * baseHourlyRate * tier2Mult),
          clauseRef: bands.clauseRefs.weekdayTier2,
        },
      ];
    }
  }
}

/* ═══════════════════════════════════════════════════════════════════════
 * Public API
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * Compute recall-to-work pay for a single recall event under the supplied
 * award-specific rules.
 *
 * Pure function — no DB IO, no side effects. The caller (server action
 * `logRecall` / `approveRecall`, or snapshotPayRun extension) supplies the
 * RateContext and resolved hourly rate.
 *
 * Phase Codex Pass A PR #B (2026-05-19) — uses the banded-OT path when
 * `rules.otBands` is set (all 4 production engines ship it). Falls back
 * to legacy single-multiplier path when absent (now fail-closed on
 * missing band).
 *
 * Returns `paidHours`, `paidAmount`, per-tier `lines[]`, and audit-trail
 * fields. The caller is responsible for:
 *   - Persisting paidHours/paidAmount on the recall event row.
 *   - Inserting a pay_run_item linked to the recall event at pay-run
 *     finalisation time.
 *   - Surfacing warnings to the coordinator in the recall approval UI.
 */
export function calculateRecallPay(
  input: RecallPayInput,
  rules: RecallRules,
  ctx: RateContext,
  baseHourlyRate: number,
  employmentType: 'PERMANENT' | 'CASUAL',
): RecallPayBreakdown {
  const warnings: string[] = [];
  const coverageGaps: EbaCoverageGap[] = [];
  const tz = input.timezone ?? 'Australia/Sydney';

  // Carveout — recall within Nh of rostered start: travel only, no minimum.
  // Nurses cl 19.7(d): "Where an employee is recalled within 3 hours of
  // their rostered commencement time, and the employee remains at work,
  // only the time spent in travelling to work will be included with the
  // actual time worked for the purposes of the overtime payment."
  //
  // Phase 1I.1 (2026-05-16) — gated on `rules.withinRosteredStartCarveoutHours > 0`.
  // HPSS cl 25.6 has no equivalent → carveoutHours = 0.
  //
  // Phase 1J MD-A5 (2026-05-16) — the carveout is cl 19.7(d) ONLY, not
  // cl 19.6. Nurses on-call recalls (cl 19.6) do NOT get the within-3h
  // carveout — they always pay the full 3h minimum. When
  // `rules.onCallScopeGating === true` AND `input.wasOnCall === true`,
  // the carveout is suppressed. See errata catalogue MD-NURSES-A5.
  const carveoutAppliesToThisRecall =
    rules.withinRosteredStartCarveoutHours > 0 &&
    !(rules.onCallScopeGating === true && input.wasOnCall === true);

  // Decide paidHours (carveout vs minimum-payment path).
  let paidHours: number;
  let carveoutApplied = false;
  if (carveoutAppliesToThisRecall && input.calledWithin3hOfRosteredStart) {
    const travelHours = travelOnlyHours(input);
    if (travelHours <= 0) {
      warnings.push(
        `Recall within ${rules.withinRosteredStartCarveoutHours}h of rostered start ` +
          `and no travel time recorded — paid hours = 0 per ${rules.clauseRef}(a).`,
      );
    }
    paidHours = travelHours;
    carveoutApplied = true;
  } else {
    let rawHours: number;
    let minimumHours: number;
    if (input.recallType === 'IN_PERSON') {
      // Phase 1J MD-A4 — travel-counts-as-time-worked is cl 19.7(c) ONLY.
      const effectiveTravelCounts =
        rules.travelCountsAsTimeWorked &&
        !(rules.onCallScopeGating === true && input.wasOnCall === true);
      rawHours = computeRawHoursInPerson(input, effectiveTravelCounts);
      minimumHours = rules.inPersonMinimumHours;
      if (rawHours <= 0) {
        warnings.push(
          `IN_PERSON recall missing workplace arrival/departure — falling back to ` +
            `minimum ${minimumHours}h per ${rules.clauseRef}.`,
        );
      }
    } else {
      rawHours = computeRawHoursElectronic(input);
      minimumHours = rules.electronicMinimumHours;
      const subsequentCount = input.electronicSubsequentCallCount ?? 0;
      if (subsequentCount > 0) {
        warnings.push(
          `${subsequentCount + 1} electronic calls within the ${minimumHours}h ` +
            `minimum window collapse to a single ${minimumHours}h minimum per FWO ` +
            `interpretation (one-occurrence-per-recall).`,
        );
      }
    }
    paidHours = Math.max(minimumHours, rawHours);
  }

  // Phase Codex Pass A PR #B (2026-05-19) — banded-OT path when otBands
  // present. All 4 production engines ship otBands as of this PR.
  if (rules.otBands) {
    const bands = rules.otBands;
    const dayCategory = selectDayCategory(
      input.recalledAt,
      tz,
      input.isPublicHoliday ?? false,
      bands,
    );

    // Stream-aware tier-1 boundary for SCHADS; other awards use the bands
    // struct's default.
    let tier1Boundary: number;
    if (rules.awardCode === 'MA000100') {
      tier1Boundary = resolveSchadsTier1Hours(
        input.classificationStream,
        input.isFullTimeForOvertime,
        bands,
      );
    } else {
      tier1Boundary = bands.weekdayTier1Hours;
    }

    const lines = buildBandedLines(
      dayCategory,
      paidHours,
      baseHourlyRate,
      ctx,
      employmentType,
      bands,
      tier1Boundary,
      coverageGaps,
    );

    const paidAmount = roundCents(lines.reduce((sum, ln) => sum + ln.amount, 0));

    // Dominant band + effective multiplier for back-compat.
    const dominantBand: RecallPayBand = dominantBandFromDayCategory(dayCategory);
    const effectiveMultiplier =
      paidHours > 0 && baseHourlyRate > 0
        ? Math.round((paidAmount / (paidHours * baseHourlyRate)) * 1000) / 1000
        : 0;

    const clauseRef = carveoutApplied
      ? `${rules.clauseRef} (within-rostered-start carveout)`
      : rules.clauseRef;

    return {
      paidHours: roundHours(paidHours),
      paidAmount,
      bandApplied: dominantBand,
      multiplierApplied: effectiveMultiplier,
      lines,
      clauseRef,
      carveoutApplied,
      ...coverageGapsField(coverageGaps),
      warnings,
    };
  }

  // ─── Legacy single-multiplier path (no otBands) ─────────────────────
  // Used only when an engine hasn't migrated to otBands. Phase Codex Pass
  // A PR #B (2026-05-19) — all 4 engines ship otBands, so this path is
  // dead in production. Retained for safety + during PR rollout.
  const legacyBand = selectLegacyBand(input.recalledAt, tz, input.isPublicHoliday ?? false);
  const multiplier = lookupOtMultiplierLegacy(ctx, legacyBand, employmentType, rules, coverageGaps);
  const casualAdditive =
    rules.casualLoadingTreatment === 'ADDITIVE' && employmentType === 'CASUAL' ? CASUAL_LOADING : 0;
  const casualMult =
    rules.casualLoadingTreatment === 'MULTIPLICATIVE' && employmentType === 'CASUAL' ? 1.25 : 1.0;
  const effectiveMult = (multiplier + casualAdditive) * casualMult;
  const paidAmount = roundCents(paidHours * baseHourlyRate * effectiveMult);

  // Emit a single legacy line so downstream consumers always have
  // `lines[]` populated.
  const legacyLineCode: RecallPayLine['code'] =
    legacyBand === 'OT_PUBLIC_HOLIDAY'
      ? 'RECALL_PAY_PUBLIC_HOLIDAY_FLAT'
      : legacyBand === 'OT_SUNDAY'
        ? 'RECALL_PAY_SUNDAY_FLAT'
        : 'RECALL_PAY_TIER1';
  const lines: RecallPayLine[] =
    paidHours > 0
      ? [
          {
            code: legacyLineCode,
            hours: roundHours(paidHours),
            multiplier: effectiveMult,
            amount: paidAmount,
            clauseRef: rules.clauseRef,
          },
        ]
      : [];

  const clauseRef = carveoutApplied
    ? `${rules.clauseRef} (within-rostered-start carveout)`
    : rules.clauseRef;

  return {
    paidHours: roundHours(paidHours),
    paidAmount,
    bandApplied: legacyBand,
    multiplierApplied: effectiveMult,
    lines,
    clauseRef,
    carveoutApplied,
    ...coverageGapsField(coverageGaps),
    warnings,
  };
}

function selectLegacyBand(recalledAt: Date, tz: string, isPublicHoliday: boolean): RecallPayBand {
  if (isPublicHoliday) return 'OT_PUBLIC_HOLIDAY';
  const dow = getDayOfWeek(recalledAt, tz);
  if (dow === 0) return 'OT_SUNDAY';
  return 'OT_TIER_1';
}

function dominantBandFromDayCategory(cat: DayCategory): RecallPayBand {
  switch (cat) {
    case 'public_holiday':
      return 'OT_PUBLIC_HOLIDAY';
    case 'sunday':
      return 'OT_SUNDAY';
    case 'saturday_ac_flat':
      return 'OT_SATURDAY_AC';
    case 'weekday_tiered':
      return 'OT_TIER_1';
  }
}

function roundCents(n: number): number {
  return roundMoney(n);
}

function roundHours(n: number): number {
  return Math.round(n * 10000) / 10000;
}

/**
 * Zero breakdown for the UnsupportedAwardEngine path. Returns explicit
 * warning so any recall event accidentally produced under an unsupported
 * award is unmistakable in audit/reconciliation output.
 */
export function zeroRecallBreakdown(originalAwardCode: string): RecallPayBreakdown {
  return {
    paidHours: 0,
    paidAmount: 0,
    bandApplied: 'OT_TIER_1',
    multiplierApplied: 0,
    lines: [],
    clauseRef: 'unsupported',
    carveoutApplied: false,
    warnings: [
      `Award ${originalAwardCode} is not yet supported — recall pay = $0. ` +
        `Workers under this award must have recall events reviewed manually before ` +
        `payroll export.`,
    ],
  };
}
